import { useListProgram } from '@/hooks/queries/use-list-program';
import { usePlanFeaturesQuery } from '@/hooks/queries/use-plan-features-query';
import { usePaywall } from '@/hooks/use-paywall';
import { PLAN_ENTITLEMENTS, PlanLimit, upgradeTierFor } from '@fluenceur/types';
import { useCallback } from 'react';

/**
 * Guards "New programme": a brand already at its plan's number of active
 * programmes meets the programme upsell (pause one, or upgrade) before it fills
 * a creation form it could not activate. The count is the one the API applies:
 * enabled programmes, including those that start later. While the plan or the
 * programmes are unread, the brand goes through: the API still refuses at
 * activation, with the same upsell.
 */
export function useProgramLimitGate() {
  const { isSuccess, planTier } = usePlanFeaturesQuery();
  const { data: programs } = useListProgram();
  const { openPlanLimitModal } = usePaywall();

  return useCallback(
    (create: () => void) => {
      const max = PLAN_ENTITLEMENTS[planTier].maxActivePrograms;
      const used = programs?.filter((program) => program.enabled).length;
      if (!isSuccess || max === null || used === undefined || used < max) {
        create();
        return;
      }
      openPlanLimitModal({
        limit: PlanLimit.ACTIVE_PROGRAMS,
        currentTier: planTier,
        targetTier: upgradeTierFor(PlanLimit.ACTIVE_PROGRAMS, planTier),
        used,
        max,
      });
    },
    [isSuccess, planTier, programs, openPlanLimitModal]
  );
}
